import { useEffect } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SUPPORTED_LANGUAGES = ['en', 'ru', 'es', 'de'] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];
const LANGUAGE_STORAGE_KEY = 'axetra_preferred_language';

interface LanguageWrapperProps {
  children: React.ReactNode;
}

export function LanguageWrapper({ children }: LanguageWrapperProps) {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  // Check if language is supported
  const isSupportedLanguage = (lang: string | undefined): lang is SupportedLanguage => {
    return lang !== undefined && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
  };

  // Get saved language preference from localStorage
  const getSavedLanguage = (): SupportedLanguage | null => {
    try {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (saved && isSupportedLanguage(saved)) {
        return saved as SupportedLanguage;
      }
    } catch (error) {
      console.warn('Failed to read language preference from localStorage:', error);
    }
    return null;
  };

  // Save language preference to localStorage
  const saveLanguage = (language: SupportedLanguage) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch (error) {
      console.warn('Failed to save language preference to localStorage:', error);
    }
  };

  useEffect(() => {
    if (lang && isSupportedLanguage(lang)) {
      // Save language preference
      saveLanguage(lang);

      // Change i18n language if different
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    }
  }, [lang, i18n]);

  // If no language or unsupported language, redirect to preferred language
  if (!lang || !isSupportedLanguage(lang)) {
    // Check saved preference first
    const savedLang = getSavedLanguage();

    // Then check browser language
    const browserLang = navigator.language.split('-')[0];

    // Determine default language: saved > browser > English
    const defaultLang = savedLang ||
                       (isSupportedLanguage(browserLang) ? browserLang : 'en');

    // Preserve the rest of the path and query string
    const pathWithoutLang = location.pathname;
    const search = location.search;
    return <Navigate to={`/${defaultLang}${pathWithoutLang}${search}`} replace />;
  }

  return <>{children}</>;
}

export { SUPPORTED_LANGUAGES };
export type { SupportedLanguage };
